import React from 'react';
import { Platform } from '../util/platform';
import { View, StyleSheet, Modal, Pressable } from 'react-native'
import { Feather } from '@expo/vector-icons';

import AppStyles from '../AppStyles';
import CustomText from './CustomText';
import { t as translate } from '../i18n';

/**
 * ConfirmationModal - Reusable confirmation dialog for destructive actions
 *
 * @param {boolean} visible - Whether the modal is visible
 * @param {string} title - Modal title
 * @param {string} message - Modal message
 * @param {string} confirmText - Text for the confirm button (default: "Confirm")
 * @param {string} cancelText - Text for the cancel button (default: "Cancel")
 * @param {string} confirmColor - Color for the confirm button (default: error color)
 * @param {string} icon - Feather icon name (default: "alert-triangle")
 * @param {Function} onConfirm - Callback when user confirms
 * @param {Function} onCancel - Callback when user cancels
 */
const ConfirmationModal = ({
  visible,
  title,
  message,
  // Default parameters run before the component body, so these resolve
  // through the module-level `t`.
  confirmText = translate('common:actions.confirm'),
  cancelText = translate('common:actions.cancel'),
  confirmColor = AppStyles.colorSet.error,
  icon = 'alert-triangle',
  onConfirm,
  onCancel,
}) => {
  if (!visible) return null;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <Pressable style={styles.overlay} onPress={onCancel}>
        <Pressable style={styles.modal} onPress={(e) => e.stopPropagation()}>
          <View style={styles.iconContainer}>
            <Feather name={icon} size={32} color={confirmColor} />
          </View>

          <CustomText
            font="semiBold"
            size={18}
            color={AppStyles.colorSet.textPrimary}
            style={styles.title}
          >
            {title}
          </CustomText>

          <CustomText
            font="regular"
            size={14}
            color={AppStyles.colorSet.textSecondary}
            style={styles.message}
          >
            {message}
          </CustomText>

          <View style={styles.buttonRow}>
            <Pressable
              style={({ hovered, focused, pressed }) => [
                styles.button,
                styles.cancelButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.cancelButtonHovered,
                pressed && { opacity: 0.85 },
              ]}
              onPress={onCancel}
              accessibilityRole="button"
              accessibilityLabel={cancelText}
            >
              <CustomText
                font="medium"
                size={14}
                color={AppStyles.colorSet.textPrimary}
              >
                {cancelText}
              </CustomText>
            </Pressable>

            <Pressable
              style={({ hovered, focused, pressed }) => [
                styles.button,
                styles.confirmButton,
                { backgroundColor: confirmColor, cursor: 'pointer' },
                (hovered || focused) && styles.confirmButtonHovered,
                pressed && { opacity: 0.85 },
              ]}
              onPress={onConfirm}
              accessibilityRole="button"
              accessibilityLabel={confirmText}
            >
              <CustomText font="medium" size={14} color="#FFFFFF">
                {confirmText}
              </CustomText>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: AppStyles.colorSet.whiteColor,
    borderRadius: 16,
    padding: 24,
    maxWidth: 400,
    width: '100%',
    alignItems: 'center',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 20,
        elevation: 8,
      },
    }),
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    textAlign: 'center',
    marginBottom: 8,
  },
  message: {
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 20,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
  },
  // Web hover/focus feedback for the modal buttons
  cancelButtonHovered: {
    backgroundColor: AppStyles.stateSet.pressed,
    borderColor: AppStyles.colorSet.textMuted,
  },
  confirmButton: {
    // backgroundColor set dynamically via confirmColor prop
  },
  confirmButtonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
});

export default ConfirmationModal;
